import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  addDays,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  isValid,
  parse,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { Box, Menu, MenuItem, ListItemText } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { Calendar as AppCalendar, CalendarEventDto, CalendarEventType } from '@/types/calendar';
import { FilterConfig } from '@components/common/PageToolbar';
import { CalendarToolbar, DateRangeView } from '@components/common/CalendarToolbar';
import { ClientCalendarEvents } from '@components/clients/calendar/ClientCalendarEvents';
import { EventCardStatus } from '@components/clients/calendar/CalendarEventStatusCard';
import { SidebarMode } from '@components/sidebar/sidebar.types';
import { WorkingHoursDto } from '@/types/appointment.types';
import { SleepCalendarFact } from '@/services/sleep.service';

export interface CalendarFetchParams {
  calendarIds: number[];
  eventTypeIds: number[];
  dateFrom: string; // yyyy-MM-dd
  dateTo: string;   // yyyy-MM-dd
}

export interface CalendarViewerContext {
  userId: number;
  panelMode: SidebarMode;
}

export interface CalendarWidgetProps {
  calendars: AppCalendar[];
  eventTypes: CalendarEventType[];
  fetchEvents: (params: CalendarFetchParams) => Promise<CalendarEventDto[]>;
  /** Who is viewing the calendar — affects which events show edit/delete controls */
  viewerContext?: CalendarViewerContext;
  workingHours?: WorkingHoursDto[];
  /** Prefix for localStorage keys. Default: `calendar-widget` */
  storagePrefix?: string;
  /** Rendered to the right of the filters (e.g., Add Event button + menu) */
  extraActions?: React.ReactNode;
  refreshTrigger?: number;
  onEventClick?: (eventId: number) => void;
  onEditEvent?: (event: CalendarEventDto) => void;
  onDeleteEvent?: (event: CalendarEventDto) => void;
  /** Called whenever the active calendar date changes */
  onCurrentDateChange?: (date: Date) => void;
  /** Called when user clicks "+" on an empty cell and selects event type */
  onAddEventWithDate?: (date: Date, eventTypeCode: string) => void;
  /** Called when user clicks "AI +" on an empty cell */
  onCellQuickAddEvent?: (date: Date, anchor: HTMLElement) => void;
  /** Called when user drag-drops an event to a new cell (copy=true when Ctrl held) */
  onEventMove?: (eventId: number, newDate: Date, copy: boolean) => void;
  /**
   * Факти сну ("Ліг" / "Встав") за [from, to) - лише перегляд (2026-09-30). Не подія календаря: окреме джерело, фільтри типів
   * подій на нього не діють, крім звуження (показуємо, лише коли вибрані всі типи). Помилка / нема доступу - без сну.
   */
  fetchSleepFacts?: (from: Date, to: Date) => Promise<SleepCalendarFact[]>;
  /**
   * Запам'ятовувати вибрану дату (2026-10-10, календар продакта): URL `?date=yyyy-MM-dd` поруч із `dateView` +
   * localStorage `${storagePrefix}-date`, щоб після перезавантаження було те саме подання. За замовчуванням вимкнено -
   * інші календарі, як і раніше, відкриваються на сьогодні.
   */
  persistDate?: boolean;
  /** Викликається, коли змінюється проміжок днів, за який календар завантажує дані. */
  onVisibleRangeChange?: (range: { dateFrom: string; dateTo: string }) => void;
  /** Додатковий вміст заголовка дня (поруч із датою), рахується із завантажених подій подання. */
  renderDayHeaderExtra?: (day: Date, events: CalendarEventDto[], compact: boolean) => React.ReactNode;
  /** Другий рядок заголовка дня, під датою. */
  renderDayHeaderSummary?: (day: Date, compact: boolean) => React.ReactNode;
  /** Позначка виконання на картці події (див. `EventCardStatus`). */
  getEventStatus?: (event: CalendarEventDto) => EventCardStatus;
}

const DATE_PARAM = 'date';
const DATE_FORMAT = 'yyyy-MM-dd';

const parseStoredDate = (value: string | null): Date | null => {
  if (!value) return null;
  const date = parse(value, DATE_FORMAT, new Date());
  return isValid(date) ? date : null;
};

export const CalendarWidget: React.FC<CalendarWidgetProps> = ({
  calendars,
  eventTypes,
  fetchEvents,
  viewerContext,
  workingHours,
  storagePrefix = 'calendar-widget',
  extraActions,
  refreshTrigger = 0,
  onEventClick,
  onEditEvent,
  onDeleteEvent,
  onCurrentDateChange,
  onAddEventWithDate,
  onCellQuickAddEvent,
  onEventMove,
  fetchSleepFacts,
  persistDate = false,
  onVisibleRangeChange,
  renderDayHeaderExtra,
  renderDayHeaderSummary,
  getEventStatus,
}) => {
  const TOOLBAR_KEY = `${storagePrefix}-toolbar`;
  const DATE_KEY = `${storagePrefix}-date`;

  const [searchParams, setSearchParams] = useSearchParams();

  const initialStoredState = (() => {
    try {
      const raw = localStorage.getItem(TOOLBAR_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();
  const storedState = useRef<Record<string, any> | null>(initialStoredState);

  const [selectedCalendars, setSelectedCalendars] = useState<number[]>([]);
  const [selectedEventTypes, setSelectedEventTypes] = useState<number[]>([]);
  const [currentDate, setCurrentDate] = useState<Date>(() => {
    if (!persistDate) return new Date();
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(DATE_KEY);
    } catch {
      stored = null;
    }
    return parseStoredDate(searchParams.get(DATE_PARAM)) ?? parseStoredDate(stored) ?? new Date();
  });
  const [dateView, setDateView] = useState<DateRangeView>(
    (searchParams.get('dateView') as DateRangeView) ||
    storedState.current?.view ||
    'week',
  );

  // Sync URL param on mount so URL always reflects the active view
  useEffect(() => {
    if (!searchParams.get('dateView') || (persistDate && !searchParams.get(DATE_PARAM))) {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        if (!next.get('dateView')) next.set('dateView', dateView);
        if (persistDate && !next.get(DATE_PARAM)) next.set(DATE_PARAM, format(currentDate, DATE_FORMAT));
        return next;
      }, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [events, setEvents] = useState<CalendarEventDto[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState<string | null>(null);

  const [cellClickDate, setCellClickDate] = useState<Date | null>(null);
  const [cellMenuAnchor, setCellMenuAnchor] = useState<HTMLElement | null>(null);

  const handleCellAddEvent = (date: Date, anchor: HTMLElement) => {
    setCellClickDate(date);
    setCellMenuAnchor(anchor);
  };

  const handleCellMenuClose = () => {
    setCellMenuAnchor(null);
    setCellClickDate(null);
  };

  const filtersInitialized = useRef(false);
  const persistReady = useRef(false);
  const prevCalendarIds = useRef<Set<number>>(new Set());

  // Initialize filter selections when data first arrives; auto-include new calendars on subsequent updates
  useEffect(() => {
    if (calendars.length === 0 || eventTypes.length === 0) return;

    if (!filtersInitialized.current) {
      const savedCals = storedState.current?.filterValues?.calendars as number[] | undefined;
      if (savedCals?.length) {
        const available = new Set(calendars.map(c => c.id));
        const valid = savedCals.filter(id => available.has(id));
        setSelectedCalendars(valid.length > 0 ? valid : calendars.map(c => c.id));
      } else {
        setSelectedCalendars(calendars.map(c => c.id));
      }

      const savedTypes = storedState.current?.filterValues?.eventTypes as number[] | undefined;
      if (savedTypes?.length) {
        const available = new Set(eventTypes.map(t => t.id));
        const valid = savedTypes.filter(id => available.has(id));
        setSelectedEventTypes(valid.length > 0 ? valid : eventTypes.map(t => t.id));
      } else {
        setSelectedEventTypes(eventTypes.map(t => t.id));
      }

      prevCalendarIds.current = new Set(calendars.map(c => c.id));
      filtersInitialized.current = true;
      persistReady.current = true;
    } else {
      // Auto-include any newly added calendars
      const newIds = calendars.map(c => c.id).filter(id => !prevCalendarIds.current.has(id));
      if (newIds.length > 0) {
        setSelectedCalendars(prev => [...prev, ...newIds]);
      }
      prevCalendarIds.current = new Set(calendars.map(c => c.id));
    }
  }, [calendars, eventTypes]);

  // Compute date range for current view
  const computeDateRange = useCallback(
    (date: Date, view: DateRangeView): { dateFrom: string; dateTo: string } => {
      let from: Date;
      let to: Date;
      switch (view) {
        case 'day':
          from = startOfDay(date);
          to = endOfDay(date);
          break;
        case 'week':
          from = startOfWeek(date, { weekStartsOn: 1 });
          to = endOfWeek(date, { weekStartsOn: 1 });
          break;
        case '2weeks':
          from = startOfWeek(date, { weekStartsOn: 1 });
          to = endOfDay(addDays(from, 13));
          break;
        case 'month':
          from = startOfWeek(startOfMonth(date), { weekStartsOn: 1 });
          to = endOfWeek(endOfMonth(date), { weekStartsOn: 1 });
          break;
        default:
          from = startOfDay(date);
          to = endOfDay(date);
      }
      return {
        dateFrom: format(from, 'yyyy-MM-dd'),
        dateTo: format(to, 'yyyy-MM-dd'),
      };
    },
    [],
  );

  const { dateFrom: visibleFrom, dateTo: visibleTo } = computeDateRange(currentDate, dateView);
  useEffect(() => {
    onVisibleRangeChange?.({ dateFrom: visibleFrom, dateTo: visibleTo });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleFrom, visibleTo]);

  const [sleepFacts, setSleepFacts] = useState<SleepCalendarFact[]>([]);

  // Факти сну - окремо від подій; лише коли фільтр типів подій не звужений (сон - не тип події).
  useEffect(() => {
    if (!persistReady.current || !fetchSleepFacts || selectedEventTypes.length !== eventTypes.length) {
      setSleepFacts([]);
      return;
    }
    const { dateFrom, dateTo } = computeDateRange(currentDate, dateView);
    const from = new Date(`${dateFrom}T00:00:00`);
    const to = addDays(new Date(`${dateTo}T00:00:00`), 1);
    let cancelled = false;
    fetchSleepFacts(from, to)
      .then(facts => { if (!cancelled) setSleepFacts(facts); })
      .catch(() => { if (!cancelled) setSleepFacts([]); });
    return () => { cancelled = true; };
  }, [fetchSleepFacts, selectedEventTypes, eventTypes.length, currentDate, dateView, refreshTrigger, persistReady.current]);

  // Fetch events whenever relevant state changes
  useEffect(() => {
    if (!persistReady.current) return;
    if (selectedCalendars.length === 0) {
      setEvents([]);
      return;
    }

    const { dateFrom, dateTo } = computeDateRange(currentDate, dateView);

    setEventsLoading(true);
    setEventsError(null);
    fetchEvents({ calendarIds: selectedCalendars, eventTypeIds: selectedEventTypes, dateFrom, dateTo })
      .then(data => {
        const unique = Array.from(new Map(data.map(e => [e.id, e])).values());
        setEvents(unique);
      })
      .catch(() => setEventsError('Не вдалося завантажити події'))
      .finally(() => setEventsLoading(false));
  }, [selectedCalendars, selectedEventTypes, currentDate, dateView, refreshTrigger, persistReady.current]);

  const handleDateChange = (date: Date) => {
    setCurrentDate(date);
    onCurrentDateChange?.(date);
    if (persistDate) {
      const value = format(date, DATE_FORMAT);
      try {
        localStorage.setItem(DATE_KEY, value);
      } catch {
        // localStorage недоступний (приватний режим) - лишається лише URL
      }
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set(DATE_PARAM, value);
        return next;
      }, { replace: true });
    }
  };

  const handleDateViewChange = (newView: DateRangeView) => {
    setDateView(newView);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('dateView', newView);
      return next;
    });
  };

  const handleResetFilters = () => {
    setSelectedCalendars(calendars.map(c => c.id));
    setSelectedEventTypes(eventTypes.map(t => t.id));
  };

  const filters: FilterConfig[] = [
    {
      id: 'calendars',
      type: 'select',
      label: 'Календарі',
      value: selectedCalendars,
      onChange: value => Array.isArray(value) && setSelectedCalendars(value),
      options: calendars.map(c => ({ value: c.id, label: c.name })),
      multiple: true,
    },
    {
      id: 'eventTypes',
      type: 'select',
      label: 'Типи подій',
      value: selectedEventTypes,
      onChange: value => Array.isArray(value) && setSelectedEventTypes(value),
      options: eventTypes.map(t => ({ value: t.id, label: t.name })),
      multiple: true,
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
      <CalendarToolbar
        storageKey={storagePrefix}
        currentDate={currentDate}
        onDateChange={handleDateChange}
        view={dateView}
        onViewChange={handleDateViewChange}
        filters={filters}
        onResetFilters={handleResetFilters}
        extraActions={extraActions}
      />

      <ClientCalendarEvents
        events={events}
        loading={eventsLoading}
        error={eventsError}
        currentDate={currentDate}
        dateView={dateView}
        viewMode="list"
        clientCalendars={calendars}
        viewerContext={viewerContext}
        workingHours={workingHours}
        onEventClick={onEventClick ?? (() => {})}
        onEdit={onEditEvent}
        onDelete={onDeleteEvent}
        onCellAddEvent={onAddEventWithDate ? handleCellAddEvent : undefined}
        onCellQuickAddEvent={onCellQuickAddEvent}
        onEventDrop={onEventMove}
        sleepFacts={sleepFacts}
        renderDayHeaderExtra={
          renderDayHeaderExtra ? (day, compact) => renderDayHeaderExtra(day, events, compact) : undefined
        }
        renderDayHeaderSummary={renderDayHeaderSummary}
        getEventStatus={getEventStatus}
      />

      <Menu
        anchorEl={cellMenuAnchor}
        open={Boolean(cellMenuAnchor)}
        onClose={handleCellMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {eventTypes.map(et => (
          <MenuItem
            key={et.id}
            onClick={() => {
              if (cellClickDate) onAddEventWithDate?.(cellClickDate, et.code);
              handleCellMenuClose();
            }}
          >
            <ListItemText primary={et.name} />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};
